// src/api/client.ts
// API-asiakas Savroc ERP backendille
// - Base URL: VITE_API_BASE_URL (env)
// - JWT Bearer token Authorization-headerissa
// - credentials: 'include' refresh-cookieta varten
// - Automaattinen token refresh 401-vastauksella

import type {
  CalendarEvent,
  CalendarEventsListResponse,
  CreateCalendarEventRequest,
  LoginRequest,
  LoginResponse,
  WeeklyPlanParseRequest,
  WeeklyPlanParseResponse,
} from './types';
import { enqueue } from '../utils/mutationQueue';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001/api';
if (!import.meta.env.VITE_API_BASE_URL && import.meta.env.PROD) {
  console.warn('[savroc-erp] VITE_API_BASE_URL puuttuu production-buildista — käytetään localhost-fallbackia. Aseta .env-tiedostossa.');
}

// ── Token storage ─────────────────────────────────────────────
let _accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  _accessToken = token;
}

export function getAccessToken(): string | null {
  return _accessToken;
}

// ── Multi-tab-identiteettivahti (4.8.2026) ────────────────────
// Refresh-cookie on selainprofiilin YHTEINEN: kun toiseen välilehteen
// kirjaudutaan eri tunnuksella, tämän välilehden seuraava refresh palauttaisi
// TOISEN käyttäjän tokenin ja tab vaihtaisi käyttäjää hiljaa (403:t + väärän
// käyttäjän data). Vahti muistaa välilehden käyttäjän (id sessionStorageen —
// EI token, joten PR #30 -linjaus "tokenit vain muistissa" säilyy) ja hylkää
// refresh-tokenin jonka sub ei täsmää → 'auth:user-changed' -tapahtuma.
const TAB_USER_KEY = 'savroc_tab_user_id';

/** JWT-payloadin sub ilman verifiointia (verifiointi kuuluu backendille). */
export function decodeTokenSub(token: string): string | null {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const sub = JSON.parse(json)?.sub;
    return typeof sub === 'string' ? sub : null;
  } catch {
    return null;
  }
}

function readTabUserId(): string | null {
  try { return sessionStorage.getItem(TAB_USER_KEY); } catch { return null; }
}

function writeTabUserId(userId: string | null): void {
  try {
    if (userId === null) sessionStorage.removeItem(TAB_USER_KEY);
    else sessionStorage.setItem(TAB_USER_KEY, userId);
  } catch {
    // sessionStorage estetty — vahti toimii silti muistivaraisesti saman
    // sivulatauksen sisällä (adoptAccessToken lukee vain storagesta; ilman
    // storagea uusi lataus adoptoi cookie-käyttäjän kuten ennenkin)
  }
}

// BroadcastChannel: kirjautuminen ilmoitetaan muille tabeille heti, jotta
// eri käyttäjän tab varoittaa välittömästi (ei vasta tokenin vanhetessa).
const AUTH_CHANNEL = 'savroc-auth';
let _authChannel: BroadcastChannel | null = null;
function authChannel(): BroadcastChannel | null {
  if (_authChannel) return _authChannel;
  try { _authChannel = new BroadcastChannel(AUTH_CHANNEL); } catch { return null; }
  return _authChannel;
}

/** Kutsutaan onnistuneen loginin jälkeen: lukitse tabin käyttäjä + ilmoita muille. */
export function registerTabUser(userId: string): void {
  writeTabUserId(userId);
  try { authChannel()?.postMessage({ type: 'login', userId }); } catch { /* ei kriittinen */ }
}

/** Kutsutaan logoutissa ja user-changed-tilanteessa. */
export function clearTabUser(): void {
  writeTabUserId(null);
}

/** Muiden välilehtien login-tapahtumien kuuntelu (auth.tsx). */
export function onOtherTabLogin(handler: (userId: string) => void): () => void {
  const ch = authChannel();
  if (!ch) return () => undefined;
  const listener = (e: MessageEvent) => {
    if (e?.data?.type === 'login' && typeof e.data.userId === 'string') handler(e.data.userId);
  };
  ch.addEventListener('message', listener);
  return () => ch.removeEventListener('message', listener);
}

/**
 * Ota refreshin palauttama token käyttöön TAI hylkää jos se kuuluu eri
 * käyttäjälle kuin tällä välilehdellä oli. Palauttaa 'user-changed' kun
 * hylättiin — kutsuja EI saa jatkaa istuntoa.
 */
export function adoptAccessToken(token: string): 'ok' | 'user-changed' {
  const sub = decodeTokenSub(token);
  const expected = readTabUserId();
  if (expected !== null && sub !== null && sub !== expected) {
    _accessToken = null;
    clearTabUser();
    window.dispatchEvent(new Event('auth:user-changed'));
    return 'user-changed';
  }
  _accessToken = token;
  if (expected === null && sub !== null) writeTabUserId(sub); // uusi tab adoptoi cookie-käyttäjän
  return 'ok';
}

// ── Core fetch wrapper ────────────────────────────────────────

interface FetchOptions extends RequestInit {
  skipAuth?: boolean;
}

async function apiFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { skipAuth, ...rest } = options;

  const headers: Record<string, string> = {
    ...(rest.headers as Record<string, string>),
  };

  // JSON body: set content-type unless multipart (browser sets it with boundary)
  if (
    rest.body &&
    !(rest.body instanceof FormData) &&
    !headers['Content-Type']
  ) {
    headers['Content-Type'] = 'application/json';
  }

  if (!skipAuth && _accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers,
    credentials: 'include', // HttpOnly refresh-cookie
  });

  // Token expired — yritä refresh kerran
  if (res.status === 401 && !skipAuth) {
    let refreshed: boolean;
    try {
      refreshed = await tryRefresh();
    } catch (e) {
      // F2: verkkovirhe refreshissä → EI uloskirjausta, näytä yhteysvirhe.
      if (e instanceof RefreshNetworkError) throw new Error('Yhteysvirhe — tarkista verkkoyhteys ja yritä uudelleen.');
      throw e;
    }
    if (refreshed) {
      headers['Authorization'] = `Bearer ${_accessToken!}`;
      const retry = await fetch(`${BASE_URL}${path}`, {
        ...rest,
        headers,
        credentials: 'include',
      });
      if (!retry.ok) throw await buildError(retry);
      return retry.status === 204 ? (undefined as T) : retry.json();
    }
    // Refresh palautti aidon auth-vanhentumisen (401/403) — tyhjennä token + ilmoita
    _accessToken = null;
    window.dispatchEvent(new Event('auth:logout'));
    throw new Error('Istunto vanhentunut. Kirjaudu uudelleen.');
  }

  if (!res.ok) throw await buildError(res);
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function buildError(res: Response): Promise<Error> {
  try {
    const body = await res.json();
    const msg = Array.isArray(body.message)
      ? body.message.join(', ')
      : body.message ?? `HTTP ${res.status}`;
    const err = new Error(msg) as Error & { statusCode: number };
    err.statusCode = res.status;
    return err;
  } catch {
    return new Error(`HTTP ${res.status}`);
  }
}

// Löydös F2: verkkovirhe refresh-kutsussa EI ole sama kuin vanhentunut istunto.
// Aiemmin `catch → false` tulkitsi kuuluvuuskadon uloskirjaukseksi. Nyt verkkovirhe
// heitetään erillisenä (RefreshNetworkError) → kutsuja EI kirjaa ulos vaan näyttää
// yhteysvirheen; vain aito 401/403 refresh-vastaus (return false) johtaa uloskirjaukseen.
export class RefreshNetworkError extends Error {
  constructor() { super('Yhteysvirhe istunnon uusinnassa'); this.name = 'RefreshNetworkError'; }
}

// Löydös F4: refresh on single-flight. Aiemmin jokainen rinnakkainen 401 laukaisi
// oman /auth/refresh-kutsun; jos backend rotatoi refresh-cookien, häviäjät saivat
// 401:n ja dispatchasivat auth:logout → satunnaisia uloskirjauksia sivun avautuessa
// (4–6 rinnakkaista hakua). Nyt kaikki rinnakkaiset 401:t jakavat saman promisen.
let _refreshInFlight: Promise<boolean> | null = null;

export async function tryRefresh(): Promise<boolean> {
  if (_refreshInFlight) return _refreshInFlight;
  _refreshInFlight = (async () => {
    let res: Response;
    try {
      res = await fetch(`${BASE_URL}/auth/refresh`, { method: 'POST', credentials: 'include' });
    } catch {
      throw new RefreshNetworkError();
    }
    if (!res.ok) return false; // aito auth-vanhentuminen (401/403)
    const data: { accessToken: string } = await res.json();
    // Identiteettivahti: eri käyttäjän token (toinen tab kirjautui) → hylkää,
    // 'auth:user-changed' on jo dispatchattu → kutsuja päätyy uloskirjaukseen
    return adoptAccessToken(data.accessToken) === 'ok';
  })();
  try {
    return await _refreshInFlight;
  } finally {
    _refreshInFlight = null;
  }
}

// ── authFetch ─────────────────────────────────────────────────
// Raw fetch-yhteensopiva wrapper, joka:
// - Lisää Authorization-headerin automaattisesti
// - Yrittää refreshin kerran 401:llä
// - Offline-tilassa POST/PUT/PATCH/DELETE -pyynnöt (JSON-bodyilla) tallennetaan
//   IndexedDB-queueseen ja synkronoidaan kun yhteys palaa — palautetaan synthetic
//   202 Accepted -response. GET-kutsut JA FormData (kuvanlataukset) epäonnistuvat
//   normaalisti.
// - Palauttaa Response-olion (kuten fetch), jotta callerit voivat käyttää res.ok / res.json()
//
// URL voi olla joko absoluuttinen (http...) tai relatiivinen (käytetään BASE_URL:ia).
/**
 * P2 (28.7.2026): tyhjennä service workerin api-cache uloskirjautuessa.
 * SW cachettaa GET /api/* -vastaukset (NetworkFirst, offline-tuki) — jaetulla
 * tehdaslaitteella seuraava käyttäjä ei saa nähdä edellisen käyttäjän dataa.
 */
export async function clearApiCache(): Promise<void> {
  try {
    if (typeof caches !== 'undefined') await caches.delete('api-cache');
  } catch {
    // Cache API puuttuu (vanha selain / ei-secure context) — ei haittaa
  }
}

export async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;

  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string>),
  };

  const method = (options.method ?? 'GET').toUpperCase();
  const isMutation = method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
  const isFormData = options.body instanceof FormData;

  if (
    options.body &&
    !isFormData &&
    !headers['Content-Type'] &&
    !headers['content-type']
  ) {
    headers['Content-Type'] = 'application/json';
  }

  if (_accessToken) {
    headers['Authorization'] = `Bearer ${_accessToken}`;
  }

  // Offline + mutaatio + JSON-body → enqueue ja palauta synthetic 202
  if (!navigator.onLine && isMutation && !isFormData) {
    return await enqueueAndRespond(fullUrl, method, options.body, headers);
  }

  // P2 (28.7.2026): GET-pyynnöille 45 s oletusaikakatkaisu — jumittunut pyyntö
  // jätti näkymän ikuiseen lataustilaan. Mutaatioihin ja AI-kutsuihin (POST,
  // voivat kestää minuutteja) ei kosketa; kutsuja voi ohittaa omalla signalilla.
  const useTimeout = method === 'GET' && !options.signal;
  const aborter = useTimeout ? new AbortController() : null;
  const timer = aborter ? window.setTimeout(() => aborter.abort(), 45_000) : null;

  let res: Response;
  try {
    res = await fetch(fullUrl, {
      ...options,
      headers,
      credentials: 'include',
      ...(aborter ? { signal: aborter.signal } : {}),
    });
  } catch (err) {
    if (aborter?.signal.aborted) {
      throw new Error('Pyyntö aikakatkaistiin — tarkista verkkoyhteys ja yritä uudelleen.');
    }
    // Network error (esim. hetzner alhaalla, DNS-fail) — kohtele kuin offlinea
    // mutaatioille; muille heitä eteenpäin
    if (isMutation && !isFormData) {
      return await enqueueAndRespond(fullUrl, method, options.body, headers);
    }
    throw err;
  } finally {
    if (timer != null) window.clearTimeout(timer);
  }

  if (res.status === 401) {
    let refreshed = false;
    try {
      refreshed = await tryRefresh();
    } catch (e) {
      // F2: verkkovirhe refreshissä → palauta alkuperäinen 401 (EI uloskirjausta).
      if (e instanceof RefreshNetworkError) return res;
      throw e;
    }
    if (refreshed) {
      headers['Authorization'] = `Bearer ${_accessToken!}`;
      res = await fetch(fullUrl, { ...options, headers, credentials: 'include' });
    } else {
      _accessToken = null;
      window.dispatchEvent(new Event('auth:logout'));
    }
  }

  return res;
}

async function enqueueAndRespond(
  url: string,
  method: string,
  body: BodyInit | null | undefined,
  headers: Record<string, string>,
): Promise<Response> {
  // Stripaa Authorization queuesta — token voi vanheta ennen synkronointia,
  // queueSync.ts lisää tuoreen tokenin synkronointihetkellä.
  const cleanHeaders: Record<string, string> = {};
  for (const [k, v] of Object.entries(headers)) {
    if (k.toLowerCase() !== 'authorization') cleanHeaders[k] = v;
  }

  const bodyStr = typeof body === 'string' ? body : null;

  await enqueue({
    url,
    method: method as 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    body: bodyStr,
    headers: cleanHeaders,
  });

  // Synthetic 202 Accepted — kerro callerille että pyyntö on vastaanotettu
  // mutta odottaa synkronointia. Body on tyhjä, joten res.json() heittää —
  // callerien tulisi tarkistaa res.ok ennen json-parsia (kuten muutenkin).
  return new Response(JSON.stringify({ queued: true, message: 'Tallennettu offline-jonoon' }), {
    status: 202,
    statusText: 'Accepted (queued offline)',
    headers: { 'Content-Type': 'application/json' },
  });
}

// ── Auth ──────────────────────────────────────────────────────

export const authApi = {
  login(body: LoginRequest): Promise<LoginResponse> {
    return apiFetch('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
      skipAuth: true,
    });
  },

  logout(): Promise<void> {
    return apiFetch('/auth/logout', { method: 'POST' });
  },

  refresh(): Promise<{ accessToken: string }> {
    return apiFetch('/auth/refresh', { method: 'POST', skipAuth: true });
  },
};

// ── 2FA / TOTP ────────────────────────────────────────────────
//
// Endpointit lähtevät ilman skipAuth:ia kun käyttäjä on jo kirjautunut
// (status/enroll/disable/regenerate). verify, initial-enroll-init ja
// initial-enroll-confirm käyttävät twoFaToken/enrollmentToken:ia eivätkä
// vaadi access-tokenia → skipAuth: true.

import type {
  TwoFactorStatusResponse,
  EnrollInitResponse,
  EnrollConfirmResponse,
  LoginResponseSuccess,
} from './types';

export const twoFactorApi = {
  /** Kirjautuneen käyttäjän 2FA-tila */
  getStatus(): Promise<TwoFactorStatusResponse> {
    return apiFetch('/auth/2fa/status');
  },

  /** Vapaaehtoinen enroll vaihe 1 — kirjautunut käyttäjä Asetukset-sivulla */
  enrollInit(): Promise<EnrollInitResponse> {
    return apiFetch('/auth/2fa/enroll-init', { method: 'POST' });
  },

  /** Vapaaehtoinen enroll vaihe 2 — vahvistus 6-numeroisella koodilla */
  enrollConfirm(code: string): Promise<EnrollConfirmResponse> {
    return apiFetch('/auth/2fa/enroll-confirm', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  /** Poista 2FA käytöstä (vaatii oikean TOTP- tai backup-koodin) */
  disable(code: string): Promise<{ disabled: true }> {
    return apiFetch('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  /** Generoi 10 uutta backup-koodia, vanhat invalidoituvat */
  regenerateBackupCodes(code: string): Promise<{ backupCodes: string[] }> {
    return apiFetch('/auth/2fa/backup-codes/regenerate', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },

  /** Login-flow:n vaihe 2: TOTP-koodi twoFaToken:lla */
  verify(twoFaToken: string, code: string, trustThisDevice = false): Promise<LoginResponseSuccess> {
    return apiFetch('/auth/2fa/verify', {
      method: 'POST',
      body: JSON.stringify({ twoFaToken, code, trustThisDevice }),
      skipAuth: true,
    });
  },

  /** C7: pakotetun enrollin vaihe 1 enrollmentToken:lla */
  initialEnrollInit(enrollmentToken: string): Promise<EnrollInitResponse> {
    return apiFetch('/auth/2fa/initial-enroll-init', {
      method: 'POST',
      body: JSON.stringify({ enrollmentToken }),
      skipAuth: true,
    });
  },

  /** C7: pakotetun enrollin vaihe 2 — palauttaa backup-koodit */
  initialEnrollConfirm(enrollmentToken: string, code: string): Promise<EnrollConfirmResponse> {
    return apiFetch('/auth/2fa/initial-enroll-confirm', {
      method: 'POST',
      body: JSON.stringify({ enrollmentToken, code }),
      skipAuth: true,
    });
  },
};

// ── Calendar ──────────────────────────────────────────────────

export const calendarApi = {
  /** GET /calendar/events */
  listEvents(params?: {
    from?: string;
    to?: string;
    project_id?: string;
    page?: number;
    limit?: number;
  }): Promise<CalendarEventsListResponse> {
    const qs = new URLSearchParams();
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.project_id) qs.set('project_id', params.project_id);
    if (params?.page) qs.set('page', String(params.page));
    if (params?.limit) qs.set('limit', String(params.limit));
    const query = qs.toString() ? `?${qs}` : '';
    return apiFetch(`/calendar/events${query}`);
  },

  /** POST /calendar/events — manuaalinen luonti */
  createEvent(body: CreateCalendarEventRequest): Promise<CalendarEvent> {
    return apiFetch('/calendar/events', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** POST /calendar/parse-weekly-plan — JSON */
  parseWeeklyPlan(body: WeeklyPlanParseRequest): Promise<WeeklyPlanParseResponse> {
    return apiFetch('/calendar/parse-weekly-plan', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  /** POST /calendar/parse-weekly-plan/upload — multipart */
  parseWeeklyPlanFile(
    file: File,
    weekStartDate?: string,
    projectId?: string,
  ): Promise<WeeklyPlanParseResponse> {
    const form = new FormData();
    form.append('file', file);
    if (weekStartDate) form.append('week_start_date', weekStartDate);
    if (projectId) form.append('project_id', projectId);
    return apiFetch('/calendar/parse-weekly-plan/upload', {
      method: 'POST',
      body: form,
    });
  },
};

// ── C4 + C6: Security extras (login events, password rotation) ───────────────

import type {
  LoginEvent,
  PasswordRotationStatus,
  SubProcessor,
  SubProcessorInput,
  MaterialComplianceReport,
  MaterialDocument,
} from './types';

export const loginEventsApi = {
  listForMe(limit = 30): Promise<LoginEvent[]> {
    return apiFetch(`/auth/login-events?limit=${limit}`);
  },
};

export const passwordRotationApi = {
  getStatus(): Promise<PasswordRotationStatus> {
    return apiFetch('/auth/password-rotation/status');
  },
};

// ── C7+: Luotetut laitteet (30 päivän cookie 2FA-ohitukseen) ────────────────

export interface TrustedDevice {
  id: string;
  userAgent: string | null;
  ipAddress: string | null;
  expiresAt: string;
  lastUsedAt: string;
  createdAt: string;
}

export const trustedDevicesApi = {
  list(): Promise<TrustedDevice[]> {
    return apiFetch('/auth/trusted-devices');
  },
  revoke(id: string): Promise<{ revoked: boolean }> {
    return apiFetch(`/auth/trusted-devices/${id}`, { method: 'DELETE' });
  },
  revokeAll(): Promise<{ revoked: number }> {
    return apiFetch('/auth/trusted-devices', { method: 'DELETE' });
  },
};

// ── D4: Sub-processors ───────────────────────────────────────────────────────

export const subProcessorsApi = {
  list(includeInactive = false): Promise<SubProcessor[]> {
    return apiFetch(`/sub-processors${includeInactive ? '?include_inactive=true' : ''}`);
  },
  get(id: string): Promise<SubProcessor> {
    return apiFetch(`/sub-processors/${id}`);
  },
  create(body: SubProcessorInput): Promise<SubProcessor> {
    return apiFetch('/sub-processors', { method: 'POST', body: JSON.stringify(body) });
  },
  update(id: string, body: Partial<SubProcessorInput>): Promise<SubProcessor> {
    return apiFetch(`/sub-processors/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  },
  archive(id: string): Promise<SubProcessor> {
    return apiFetch(`/sub-processors/${id}`, { method: 'DELETE' });
  },
};

// ── D9 + D13: Material compliance + documents ────────────────────────────────

export const complianceApi = {
  getMaterialReport(materialId: string): Promise<MaterialComplianceReport> {
    return apiFetch(`/compliance/materials/${materialId}`);
  },

  // D8 ISO 9001:2026 -raportit
  getIso9001Overview(from?: string, to?: string): Promise<ManagementReviewReport> {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const qs = q.toString();
    return apiFetch(`/compliance/iso9001/overview${qs ? `?${qs}` : ''}`);
  },

  getIso9001InternalAudit(from?: string, to?: string): Promise<InternalAuditReport> {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const qs = q.toString();
    return apiFetch(`/compliance/iso9001/internal-audit${qs ? `?${qs}` : ''}`);
  },

  getIso9001SupplierEvaluation(from?: string, to?: string): Promise<SupplierEvaluationReport> {
    const q = new URLSearchParams();
    if (from) q.set('from', from);
    if (to) q.set('to', to);
    const qs = q.toString();
    return apiFetch(`/compliance/iso9001/supplier-evaluation${qs ? `?${qs}` : ''}`);
  },
};

// ── D8 ISO 9001 -raporttityypit ─────────────────────────────────────────────

export interface NcrMetrics {
  total: number;
  open: number;
  closed: number;
  bySeverity: { LOW: number; MEDIUM: number; HIGH: number; CRITICAL: number };
  byCategory: Record<string, number>;
  avgClosureTimeDays: number | null;
  overdueCapaCount: number;
  rootCausePercent: number;
}

export interface CalibrationCompliance {
  totalDevices: number;
  upToDate: number;
  dueSoon: number;
  overdue: number;
  neverCalibrated: number;
  compliancePercent: number;
  overdueDevices: Array<{
    deviceId: string;
    deviceName: string;
    daysOverdue: number;
  }>;
}

export interface SupplierEvaluation {
  totalSuppliers: number;
  cleanSuppliers: number;
  highRiskSuppliers: number;
  worstPerformers: Array<{
    supplierId: string;
    supplierName: string;
    deviationCount: number;
    purchaseOrderCount: number;
    defectRate: number | null;
  }>;
}

export interface AuditTrailSummary {
  totalEvents: number;
  byAction: Record<string, number>;
  topResources: Array<{ resourceType: string; eventCount: number }>;
  uniqueUsers: number;
}

export interface ManagementReviewReport {
  period: { from: string; to: string };
  generatedAt: string;
  reportId: string;
  nonconformity: NcrMetrics;
  calibration: CalibrationCompliance;
  supplier: SupplierEvaluation;
  auditTrail: AuditTrailSummary;
  narrative: string;
}

export interface InternalAuditReport {
  period: { from: string; to: string };
  nonconformity: NcrMetrics;
  auditTrail: AuditTrailSummary;
}

export interface SupplierEvaluationReport {
  period: { from: string; to: string };
  evaluation: SupplierEvaluation;
}

export const materialDocumentsApi = {
  list(materialId: string): Promise<MaterialDocument[]> {
    return apiFetch(`/materials/${materialId}/documents`);
  },
  attach(
    materialId: string,
    body: {
      documentId: string;
      documentCategory: string;
      validFrom?: string;
      validUntil?: string;
    },
  ): Promise<MaterialDocument> {
    return apiFetch(`/materials/${materialId}/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
};
